import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'

import { QUERY_KEYS } from 'constants/constants'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator } from '..'
import { getJobCategory } from '../../constants/job-categories'
import { usePartnerContext, usePaymentContext } from '../../context'
import { query } from '../../graphql'
import {
  getScoreColorV1,
  getScoreColorV2,
  INVISIBLE
} from '../../helpers/score-color'
import NetworkError from '../network-error'
import Panel from '../tailwind/Panel'
import CareersMatching from './careers-matching'

const TRANSLATION_LOCATION = 'pages.talent.'

const getScoreValueAndColor = ({ partner, career, t }) => {
  const { isV1ScoreVisible, isV2ScoreVisible } = partner
  const { matching_score, complete_dataset, diagnostic_version } = career

  const result = {
    // default properties
    value: t('talents.crossmode.complete_dataset_false'),
    color: INVISIBLE,
    complete_dataset
  }

  if (!complete_dataset) return result

  const value = Math.round(matching_score)

  if (diagnostic_version === 1) {
    result.color = getScoreColorV1(value)

    if (isV1ScoreVisible) result.value = value
    else result.value = '-'
  } else if (diagnostic_version === 2) {
    result.color = getScoreColorV2({ career, value })

    if (isV2ScoreVisible) result.value = value
    else result.value = '-'
  }

  return result
}

const CareersMatchingContainer = ({ space, refetchSpace }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { crossComparison } = usePaymentContext()

  const [showArchivedCareers, setShowArchivedCareers] = useState(false)

  const queryMatchMeIfYouCanEnabled = !!space.owner && space.progress === 100

  const {
    status,
    data: rawData,
    error: queryError
  } = useQuery({
    queryKey: QUERY_KEYS.matchMeIfYouCanByOwnerAndPartner(
      space.owner,
      partner.id
    ),
    queryFn: () =>
      query({
        query: 'matchMeIfYouCan',
        variables: { partner_id: partner.id, user_id: space.owner }
      }),
    enabled: queryMatchMeIfYouCanEnabled,
    select: (response) => {
      const result = JSON.parse(response)
        .map((career) => ({
          ...career,
          categoryName: getJobCategory(career.category),
          score: getScoreValueAndColor({ career, partner, t })
        }))
        .sort((a, b) => {
          if (!a.complete_dataset || !b.complete_dataset) return -1
          return b.score.value - a.score.value
        })

      if (!crossComparison) {
        return result
          .filter(({ complete_dataset }) => complete_dataset)
          .slice(0, 5)
      }

      return result
    },
    refetchOnWindowFocus: false
  })

  // Filter data based on showArchivedCareers toggle
  const data = rawData
    ? rawData.filter((career) => showArchivedCareers || !career.archived)
    : []

  // Check if Matching is visible based on partner settings
  if (!partner.isDashboardComponentVisible?.matching) {
    return null
  }

  if (queryError) return <NetworkError />

  if (queryMatchMeIfYouCanEnabled && status === 'pending') {
    return <ActivityIndicator className='mx-auto my-16' />
  }

  if (space.progress < 100 && crossComparison) {
    return (
      <Panel>
        <div className='flex w-full items-center justify-center'>
          <p className='max-w-lg text-center text-sm italic text-gray-500'>
            {t(TRANSLATION_LOCATION + 'careers_matching_progress_smaller_100')}
          </p>
        </div>
      </Panel>
    )
  }

  return (
    <CareersMatching
      {...{
        space,
        refetchSpace,
        data: data || [],
        showArchivedCareers,
        setShowArchivedCareers
      }}
    />
  )
}

CareersMatchingContainer.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired
}

export default CareersMatchingContainer
